import { extractPaginationState } from "api/mappers";
import type { PaginatedResult, PaginationState } from "api/types";
import type {
  Conversation,
  ConversationFormData,
  ConversationRow,
  ConversationTabCounts,
} from "conversations/types";
import { selectedFiltersToQueryParams } from "shared/mappers/filter.mapper";
import type { SelectedFilters } from "shared/types";
import { ref } from "vue";

/**
 * The endpoints a Gesprekken list talks to. Each store supplies its own.
 *
 * `context` is what the list is scoped to, or null when it is scoped to nothing: the
 * mentorklas code (or `all`) for the mentor tab, the schedule id for the lesgroep tab, and
 * null for the teacher's own overview, which spans every class. A store that needs one
 * runs its argument through `requireConversationContext` — see the note there.
 *
 * There is no filters fetcher: the gesprekstype options come from
 * `useConversationTypeStore`, which caches them itself.
 */
export type ConversationRowFetchers = {
  fetchList: (
    params: Record<string, string>,
    context: string | null,
  ) => Promise<PaginatedResult<ConversationRow>>;
  fetchCounts: (
    params: Record<string, string>,
    context: string | null,
  ) => Promise<ConversationTabCounts>;
  createNote: (
    formData: ConversationFormData,
    context: string | null,
  ) => Promise<Conversation>;
};

/**
 * A class-scoped list must know its class before anything is fetched. Fails loudly rather
 * than building a malformed URL (e.g. `my-lessongroups//conversations`). Applied by the
 * classroom stores at each fetcher, rather than by the composable, because the teacher's
 * own overview legitimately has no context and would otherwise need a placeholder one.
 */
export const requireConversationContext = (context: string | null): string => {
  if (context === null || context === "") {
    throw new Error(
      "Class-scoped conversation store used without a context; call setContext first.",
    );
  }

  return context;
};

type FetchingMap = {
  rows: false | Promise<PaginatedResult<ConversationRow>>;
  counts: false | Promise<ConversationTabCounts>;
};

const emptyCounts = (): ConversationTabCounts => ({ planned: 0, completed: 0 });

/**
 * `force` bypasses the in-flight guard.
 *
 * The guard reuses a request with the same context and params, which is right for a tab
 * switch or a repeated keystroke but wrong straight after a save: the in-flight request
 * was issued *before* the new gesprek existed, so reusing it renders the list without it
 * and never refetches. A refresh that follows a mutation therefore forces its own call.
 */
type LoadOptions = { force?: boolean };

/**
 * The state and fetching behind one Gesprekken table: the rows and their page, the tab
 * counts, the in-flight guards, and the note create.
 *
 * Identical for a mentorklas, a lesgroep and the teacher's own overview, so it lives here
 * rather than three times. Each store is a real `defineStore` of its own that calls this
 * with its own endpoints — a reader opening any of them sees which endpoints it talks to
 * without chasing a factory argument.
 */
export const useConversationRowList = (fetchers: ConversationRowFetchers) => {
  const rows = ref<ConversationRow[]>([]);
  const rowsPagination = ref<PaginationState | null>(null);
  const counts = ref<ConversationTabCounts>(emptyCounts());

  const context = ref<string | null>(null);

  const isFetching = ref<FetchingMap>({ rows: false, counts: false });

  // The params of the in-flight request. Used for dedup only — see the generation
  // counters below for which response is allowed to write.
  let rowsKey: string | null = null;
  let countsKey: string | null = null;

  /*
   * Which response may write. The key alone cannot decide that: `force` deliberately puts
   * a second request with the SAME key in flight, so both would pass a key check and the
   * slower one would win. That is the actual post-save bug — the pre-save response landing
   * last and dropping the gesprek the teacher just created, while the tab counts still
   * count it. A response applies only while its generation is the newest one issued.
   */
  let rowsGeneration = 0;
  let countsGeneration = 0;

  const $reset = () => {
    rows.value = [];
    rowsPagination.value = null;
    counts.value = emptyCounts();
    rowsKey = null;
    countsKey = null;
    // Bumped so a request still in flight cannot write into the class we just left, and
    // cleared here as well because that request will no longer clear it itself.
    rowsGeneration += 1;
    countsGeneration += 1;
    isFetching.value = { rows: false, counts: false };
  };

  // Point the store at a different class. Clears cached data so the next fetch reloads.
  const setContext = (next: string | null) => {
    if (next === context.value) {
      return;
    }

    context.value = next;
    $reset();
  };

  const loadRows = async (
    filters: SelectedFilters = {},
    { force = false }: LoadOptions = {},
  ): Promise<PaginatedResult<ConversationRow>> => {
    const params = selectedFiltersToQueryParams(filters);
    const key = JSON.stringify([context.value, params]);

    if (force === false && isFetching.value.rows !== false && rowsKey === key) {
      return isFetching.value.rows;
    }

    rowsKey = key;
    const generation = ++rowsGeneration;

    isFetching.value.rows = (async () => {
      try {
        const result = await fetchers.fetchList(params, context.value);

        if (generation === rowsGeneration) {
          rows.value = result.items;
          rowsPagination.value = extractPaginationState(result);
        }

        return result;
      } finally {
        if (generation === rowsGeneration) {
          isFetching.value.rows = false;
        }
      }
    })();

    return isFetching.value.rows;
  };

  const loadCounts = async (
    filters: SelectedFilters = {},
    { force = false }: LoadOptions = {},
  ): Promise<ConversationTabCounts> => {
    // The counts cover both tabs and every page at once, so status and pagination are
    // both irrelevant to them: dropping all three keeps one cached result per search
    // instead of refetching on every tab switch and every page step.
    const {
      status: _status,
      page: _page,
      per_page: _perPage,
      ...rest
    } = filters;
    const params = selectedFiltersToQueryParams(rest);
    const key = JSON.stringify([context.value, params]);

    if (
      force === false &&
      isFetching.value.counts !== false &&
      countsKey === key
    ) {
      return isFetching.value.counts;
    }

    countsKey = key;
    const generation = ++countsGeneration;

    isFetching.value.counts = (async () => {
      try {
        const result = await fetchers.fetchCounts(params, context.value);

        if (generation === countsGeneration) {
          counts.value = result;
        }

        return result;
      } finally {
        if (generation === countsGeneration) {
          isFetching.value.counts = false;
        }
      }
    })();

    return isFetching.value.counts;
  };

  /**
   * The list is not patched optimistically: a new gesprek may belong to the other tab (a
   * date in the past), and the counts have to move with it — the caller refetches instead.
   */
  const createNote = async (
    formData: ConversationFormData,
  ): Promise<Conversation> => fetchers.createNote(formData, context.value);

  return {
    rows,
    rowsPagination,
    counts,
    context,
    isFetching,
    setContext,
    loadRows,
    loadCounts,
    createNote,
    $reset,
  };
};
